import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { keyframes } from '@mui/system';
import { motion, AnimatePresence } from 'framer-motion';

const fadeSweep = keyframes`
  0% { opacity: 0.08; transform: scaleX(0.25); transform-origin: left center; }
  100% { opacity: 0; transform: scaleX(1); transform-origin: left center; }
`;

const LOGO_SRC = '/assets/logos/bslogo.png';
const STORAGE_KEY = 'boldcomp_launch_once';

export default function LaunchScreen({ logoUrl = LOGO_SRC }) {
  const [visible, setVisible] = useState(true);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    try {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        setReduceMotion(true);
        setVisible(false);
        return;
      }
    } catch (_) {}

    const skip = sessionStorage.getItem(STORAGE_KEY) === '1';
    if (skip) {
      setVisible(false);
      return;
    }

    const minMs = 900;
    const start = performance.now();
    const t = window.setTimeout(() => {
      const elapsed = performance.now() - start;
      const wait = Math.max(0, minMs - elapsed);
      window.setTimeout(() => {
        setVisible(false);
        try {
          sessionStorage.setItem(STORAGE_KEY, '1');
        } catch (_) {}
      }, wait);
    }, 50);

    return () => window.clearTimeout(t);
  }, []);

  if (reduceMotion) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="launch"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.55, ease: [0.4, 0, 0.2, 1] }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 20000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(165deg, #12133a 0%, #1D1D4E 42%, #162043 100%)',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              overflow: 'hidden',
              pointerEvents: 'none',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: '42%',
                left: '15%',
                right: '15%',
                height: 2,
                bgcolor: 'secondary.main',
                opacity: 0.25,
                animation: `${fadeSweep} 1.1s ease-out forwards`,
              },
            }}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.88, filter: 'blur(8px)' }}
            animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
            transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
          >
            <Box
              component="img"
              src={logoUrl}
              alt=""
              sx={{
                width: { xs: 160, sm: 200 },
                height: 'auto',
                display: 'block',
                filter: 'drop-shadow(0 12px 32px rgba(0,0,0,0.35))',
              }}
              onError={(e) => {
                e.target.style.opacity = 0.4;
              }}
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
